import React, { useEffect, useMemo, useRef, useState } from "react";
import "../css/wordle.css";
import WORD_LIST from "../assets/wordle_words.json";

const emptyGuesses = () => Array.from({ length: 6 }, () => "");
const emptyStatuses = () => Array.from({ length: 6 }, () => []);
const STORAGE_KEY = "troydle-state";

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const getDailyWord = () => {
  const today = getTodayKey();
  const epoch = new Date("2022-01-01T00:00:00Z");
  const todayDate = new Date(`${today}T00:00:00Z`);
  const dayIndex = Math.max(0, Math.floor((todayDate - epoch) / 86400000));
  const word = WORD_LIST[dayIndex % WORD_LIST.length] || WORD_LIST[0] || "CRANE";
  return word.toUpperCase();
};

function evaluateGuess(guess, answer) {
  const statuses = Array(guess.length).fill("absent");
  const answerChars = answer.split("");
  const used = Array(answer.length).fill(false);

  for (let i = 0; i < guess.length; i += 1) {
    if (guess[i] === answer[i]) {
      statuses[i] = "correct";
      used[i] = true;
    }
  }

  for (let i = 0; i < guess.length; i += 1) {
    if (statuses[i] === "correct") continue;
    const idx = answerChars.findIndex((char, index) => char === guess[i] && !used[index]);
    if (idx !== -1) {
      statuses[i] = "present";
      used[idx] = true;
    }
  }

  return statuses;
}

function Wordle() {
  const [answer, setAnswer] = useState(getDailyWord());
  const [guesses, setGuesses] = useState(emptyGuesses);
  const [statuses, setStatuses] = useState(emptyStatuses);
  const [rowIndex, setRowIndex] = useState(0);
  const [currentGuess, setCurrentGuess] = useState("");
  const [message, setMessage] = useState("");
  const [gameState, setGameState] = useState("playing");
  const [hydrated, setHydrated] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const today = getTodayKey();
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.date === today) {
          setAnswer(parsed.answer || getDailyWord());
          setGuesses(parsed.guesses || emptyGuesses());
          setStatuses(parsed.statuses || emptyStatuses());
          setRowIndex(parsed.rowIndex ?? 0);
          setCurrentGuess(parsed.currentGuess || "");
          setGameState(parsed.gameState || "playing");
          setHydrated(true);
          return;
        }
      } catch (error) {
        // ignore invalid storage
      }
    }
    setAnswer(getDailyWord());
    setGuesses(emptyGuesses());
    setStatuses(emptyStatuses());
    setRowIndex(0);
    setCurrentGuess("");
    setGameState("playing");
    setHydrated(true);
  }, []);

  const dictionary = useMemo(() => new Set(WORD_LIST.map((word) => word.toUpperCase())), []);

  const updateMessage = (text) => {
    setMessage(text);
    if (text) {
      setTimeout(() => setMessage(""), 1800);
    }
  };

  const submitGuess = () => {
    if (gameState !== "playing") return;
    if (currentGuess.length !== 5) {
      updateMessage("Need 5 letters.");
      return;
    }
    if (!dictionary.has(currentGuess)) {
      updateMessage("Not in word list.");
      return;
    }

    const nextGuesses = [...guesses];
    const nextStatuses = [...statuses];
    nextGuesses[rowIndex] = currentGuess;
    nextStatuses[rowIndex] = evaluateGuess(currentGuess, answer);

    setGuesses(nextGuesses);
    setStatuses(nextStatuses);

    if (currentGuess === answer) {
      setGameState("won");
      updateMessage("Nice! You got it.");
      return;
    }

    if (rowIndex === 5) {
      setGameState("lost");
      updateMessage(`Answer: ${answer}`);
      return;
    }

    setRowIndex(rowIndex + 1);
    setCurrentGuess("");
  };

  const handleKey = (key) => {
    if (gameState !== "playing") return;
    if (key === "ENTER") {
      submitGuess();
      return;
    }
    if (key === "BACK") {
      setCurrentGuess((prev) => prev.slice(0, -1));
      return;
    }
    if (/^[A-Z]$/.test(key) && currentGuess.length < 5) {
      setCurrentGuess((prev) => prev + key);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        return;
      }
      if (event.key === "Enter") {
        handleKey("ENTER");
      } else if (event.key === "Backspace") {
        handleKey("BACK");
      } else if (/^[a-zA-Z]$/.test(event.key)) {
        handleKey(event.key.toUpperCase());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  useEffect(() => {
    if (!hydrated) return;
    const payload = {
      date: getTodayKey(),
      answer,
      guesses,
      statuses,
      rowIndex,
      currentGuess,
      gameState,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [answer, guesses, statuses, rowIndex, currentGuess, gameState, hydrated]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (event) => {
    if (gameState !== "playing") return;
    const next = event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5);
    setCurrentGuess(next);
  };

  return (
    <div className="wordle-page">
      <div className="wordle-shell">
        <p className="wordle-subtitle">Guess the 5-letter word in 6 tries</p>

        <div className="wordle-toast-slot">
          {message && <div className="wordle-toast">{message}</div>}
        </div>

        <div className="wordle-board" onClick={focusInput} onTouchStart={focusInput}>
          <input
            ref={inputRef}
            className="wordle-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleKey("ENTER");
              }
            }}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-label="Wordle input"
          />
          {Array.from({ length: 6 }).map((_, row) => {
            const letters = row === rowIndex ? currentGuess.split("") : guesses[row].split("");
            const rowStatuses = statuses[row] || [];
            return (
              <div className="wordle-row" key={`row-${row}`}>
                {Array.from({ length: 5 }).map((_, col) => {
                  const letter = letters[col] || "";
                  const status = rowStatuses[col] || "";
                  const isActive = row === rowIndex && col === letters.length;
                  return (
                    <div
                      key={`cell-${row}-${col}`}
                      className={`wordle-cell ${status} ${isActive ? "active" : ""}`}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Wordle;
